import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useSession } from '@/contexts/auth/session'
import { useLogout } from '@/contexts/auth/use-logout'
import { useUser } from '@/contexts/auth/user'
import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { HttpError } from '@/services/fintalk/api/types/errors/HttpError'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import { getUser } from '@/services/fintalk/services/fintalk-backoffice/users/get'

type HydrateUserLoadingState = { type: 'loading' }

type HydrateUserSuccessState = { type: 'success' }

type HydrateUserErrorState = {
  type: 'error'
  errorType:
    | 'NETWORK_ERROR'
    | 'UNAUTHORIZED'
    | 'UNKNOWN_ERROR'
    | 'USER_INACTIVE'
  eventId?: string
}

type HydrateUserState =
  | HydrateUserLoadingState
  | HydrateUserSuccessState
  | HydrateUserErrorState

export function useHydrateUser() {
  const companyId = useRouteCompanyId()!

  const {
    state: { userId },
  } = useSession()

  const { setUser } = useUser()

  const logout = useLogout()

  const [state, setState] = useState<HydrateUserState>({ type: 'loading' })

  const latestCompanyId = useRef<string | null>(null)

  const hydrate = useCallback(
    async (companyId: string, userId: string) => {
      latestCompanyId.current = companyId

      setState({ type: 'loading' })

      try {
        const user = await getUser(companyId, userId)

        if (latestCompanyId.current !== companyId) return

        if (!user.active) {
          logout()
          setState({ type: 'error', errorType: 'USER_INACTIVE' })
          return
        }

        setUser(user)
        setState({ type: 'success' })
      } catch (error) {
        if (latestCompanyId.current !== companyId) return

        if (error instanceof NetworkError) {
          setState({ type: 'error', errorType: 'NETWORK_ERROR' })
          return
        }

        if (
          error instanceof HttpError &&
          [401, 403].includes(error.response?.statusCode ?? 0)
        ) {
          logout()
          setState({ type: 'error', errorType: 'UNAUTHORIZED' })
          return
        }

        const eventId = Sentry.captureException(error)

        setState({ type: 'error', errorType: 'UNKNOWN_ERROR', eventId })
      }
    },
    [setUser, logout],
  )

  useEffect(() => {
    if (!userId) return

    hydrate(companyId, userId)
  }, [companyId, userId, hydrate])

  const retry = useCallback(() => {
    if (companyId && userId) hydrate(companyId, userId)
  }, [companyId, userId, hydrate])

  return { state, userId, retry }
}
